import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import useOutsideClick from "../hooks/useOutsideClick";

// Shared "action menu" popup used by row-level action icons (e.g. Manage my
// reports, Document library). Renders a `section-card action-popup` card
// with a list of menu entries and closes itself when the user clicks
// anywhere outside the popup and its trigger. Pass `toggleRef` (a ref to
// the icon/button that opens the popup) so clicking that trigger again
// isn't treated as an outside click — the trigger's own onClick handler is
// then free to toggle the popup closed itself.
//
// Rendered via a portal into document.body, positioned with `position:
// fixed` computed from the trigger's bounding rect. Both tables this popup
// is used in clip/scroll their contents (overflow, PerfectScrollbar), so a
// popup nested inside the table's own DOM gets visually clipped or hidden
// once the row scrolls near the table's edge. Portaling it out of the
// table entirely avoids that, and the scroll/resize listeners keep it
// anchored to the trigger icon if the page or table scrolls while open.
//
// Pass `containerRef` (a ref to the surrounding card/section) to keep the
// popup from spilling outside that container's edges — its position is
// clamped against the container's bounding rect once the popup's own size
// is known.
export default function ActionPopup({
    onClose,
    items,
    toggleRef,
    containerRef,
    style,
    className,
}) {
    const popupRef = useRef(null);
    const isClickedOutside = useOutsideClick(popupRef, toggleRef);
    const [rawPosition, setRawPosition] = useState(null);
    const [position, setPosition] = useState(null);

    useEffect(() => {
        if (isClickedOutside) {
            onClose();
        }
    }, [isClickedOutside, onClose]);

    useLayoutEffect(() => {
        const updateRawPosition = () => {
            const trigger = toggleRef?.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            setRawPosition({ top: rect.bottom, left: rect.left });
        };

        updateRawPosition();
        // capture: true so scrolling inside the table's own scroll
        // container (e.g. PerfectScrollbar) also triggers a reposition,
        // not just window-level scrolling.
        window.addEventListener("scroll", updateRawPosition, true);
        window.addEventListener("resize", updateRawPosition);

        return () => {
            window.removeEventListener("scroll", updateRawPosition, true);
            window.removeEventListener("resize", updateRawPosition);
        };
    }, [toggleRef]);

    // Runs after the popup has rendered at `rawPosition` (so its actual
    // size can be measured) and, when a `containerRef` was supplied,
    // clamps the position so the popup never spills outside the
    // container's bounds. Synchronous (useLayoutEffect) so the correction
    // is applied before the browser paints — no visible flicker.
    useLayoutEffect(() => {
        if (!rawPosition) return;

        const container = containerRef?.current;
        const popup = popupRef.current;

        if (!container || !popup) {
            setPosition(rawPosition);
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const popupRect = popup.getBoundingClientRect();

        const minLeft = containerRect.left;
        const maxLeft = Math.max(
            minLeft,
            containerRect.right - popupRect.width
        );
        const minTop = containerRect.top;
        const maxTop = Math.max(
            minTop,
            containerRect.bottom - popupRect.height
        );

        setPosition({
            top: Math.min(Math.max(rawPosition.top, minTop), maxTop),
            left: Math.min(Math.max(rawPosition.left, minLeft), maxLeft),
        });
    }, [rawPosition, containerRef]);

    const visibleItems = items.filter((item) => item.visible !== false);

    if (!rawPosition) {
        return null;
    }

    return ReactDOM.createPortal(
        <div
            className={
                "section-card action-popup" + (className ? " " + className : "")
            }
            style={{
                ...style,
                position: "fixed",
                top: (position ?? rawPosition).top,
                left: (position ?? rawPosition).left,
            }}
            ref={popupRef}
        >
            {visibleItems.map((item, index) => (
                <button
                    key={item.key ?? index}
                    type="button"
                    className={
                        "cursor-pointer" +
                        (item.className ? " " + item.className : "")
                    }
                    onClick={item.onClick}
                >
                    {item.label}
                </button>
            ))}
        </div>,
        document.body
    );
}

ActionPopup.propTypes = {
    onClose: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node.isRequired,
            onClick: PropTypes.func.isRequired,
            visible: PropTypes.bool,
            className: PropTypes.string,
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ).isRequired,
    toggleRef: PropTypes.shape({
        current: PropTypes.any,
    }),
    containerRef: PropTypes.shape({
        current: PropTypes.any,
    }),
    style: PropTypes.object,
    className: PropTypes.string,
};

ActionPopup.defaultProps = {
    toggleRef: undefined,
    containerRef: undefined,
    style: undefined,
    className: "",
};
